import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { AxiosError, AxiosResponse } from "axios";
import { RootState } from "../store";
import axiosInstance from "src/apis/axiosInstance";
import { APIResponseCover } from "./auth.d";
import { readAPIErrorMessage, throwErrApi } from "src/utils/errorUtil";
import { API_STATUS } from "src/utils/constants";

export enum CreditType {
  Given = 1,
  Received = 2,
}

export enum CreditStatus {
  Active = 1,
  PartiallyUsed = 2,
  FullyUsed = 3,
  Expired = 4,
  Cancelled = 5,
}

export const CREDIT_TYPE_LABEL: Record<number, string> = {
  [CreditType.Given]: "Credit Given",
  [CreditType.Received]: "Credit Received",
};

export const CREDIT_STATUS_LABEL: Record<number, string> = {
  [CreditStatus.Active]: "Active",
  [CreditStatus.PartiallyUsed]: "Partially Used",
  [CreditStatus.FullyUsed]: "Fully Used",
  [CreditStatus.Expired]: "Expired",
  [CreditStatus.Cancelled]: "Cancelled",
};

export const CREDIT_STATUS_COLOR: Record<number, string> = {
  [CreditStatus.Active]: "success",
  [CreditStatus.PartiallyUsed]: "warning",
  [CreditStatus.FullyUsed]: "secondary",
  [CreditStatus.Expired]: "danger",
  [CreditStatus.Cancelled]: "dark",
};

export type PartnerType =
  | "hotel"
  | "cab"
  | "taxi"
  | "flight_agency"
  | "cruise"
  | "insurance"
  | "agent";

export const PARTNER_TYPE_LABEL: Record<PartnerType, string> = {
  hotel: "Hotel",
  cab: "Cab Vendor",
  taxi: "Taxi",
  flight_agency: "Flight Agency",
  cruise: "Cruise Line",
  insurance: "Insurance Provider",
  agent: "Travel Agent",
};

// Partner FK field name as expected by the API
export const PARTNER_TYPE_FK_FIELD: Record<PartnerType, string> = {
  hotel: "hotel",
  cab: "cab_vendor",
  taxi: "taxi",
  flight_agency: "flight_agency",
  cruise: "cruise_line",
  insurance: "insurance_provider",
  agent: "agent",
};

export interface CreditNote {
  id: string;
  reference_number: string;
  credit_type: CreditType;
  status: CreditStatus;
  booking: string | null;
  customer_name: string | null;
  customer_number: number | null;
  customer_email: string | null;
  partner_type: PartnerType | null;
  partner_name: string | null;
  partner_city: string | null;
  hotel: string | null;
  cab_vendor: string | null;
  taxi: string | null;
  flight_agency: string | null;
  cruise_line: string | null;
  insurance_provider: string | null;
  agent: string | null;
  amount: string;
  used_amount: string;
  currency: string;
  reason: string | null;
  expiry_date: string | null;
  notes: string | null;
  created_date: string;
  updated_date: string;
}

export interface CreditNoteUsage {
  id: string;
  amount_used: string;
  booking: { id: string; customer_name: string; [key: string]: any } | null;
  user: { uuid: string; name: string; [key: string]: any };
  notes: string | null;
  created_date: string;
}

export interface CreditNoteDetail extends Omit<CreditNote, "booking"> {
  booking: { id: string; customer_name: string; [key: string]: any } | null;
  usages: CreditNoteUsage[];
}

export interface CreditNoteAddForm {
  credit_type: CreditType | "";
  amount: string;
  currency?: string;
  booking?: string;
  reason?: string;
  expiry_date?: string | null;
  notes?: string;
  // credit_type = 1 (Given)
  customer_name?: string;
  customer_number?: string;
  customer_email?: string;
  // credit_type = 2 (Received)
  partner_type?: PartnerType | "";
  hotel?: string;
  cab_vendor?: string;
  taxi?: string;
  flight_agency?: string;
  cruise_line?: string;
  insurance_provider?: string;
  agent?: string;
}

export interface CreditNoteUpdateForm {
  id: string;
  reason?: string;
  notes?: string;
  expiry_date?: string | null;
  booking?: string;
  status?: 5;
  // credit_type = 1
  customer_name?: string;
  customer_number?: string;
  customer_email?: string;
  // credit_type = 2
  partner_type?: PartnerType | "";
  hotel?: string;
  cab_vendor?: string;
  taxi?: string;
  flight_agency?: string;
  cruise_line?: string;
  insurance_provider?: string;
  agent?: string;
}

export interface UseCreditForm {
  amount_used: string;
  booking?: string;
  notes?: string;
}

export interface CreditNoteListFilter {
  credit_type?: number | "";
  status?: number | "";
  partner_type?: string;
  customer_number?: string;
  booking_id?: string;
  from_date?: string;
  to_date?: string;
}

export interface CreditNoteReportFilter {
  from_date?: string;
  to_date?: string;
  credit_type?: number | "";
  status?: number | "";
  group_by?: "month" | "user" | "partner" | "";
}

export interface CreditNoteReportItem {
  // group_by = month
  month?: string;
  credit_type?: number;
  count?: number;
  total_amount?: string;
  total_used?: string;
  total_remaining?: string;
  // group_by = user
  user_id?: string;
  user__name?: string;
  // group_by = partner
  partner_type?: string;
}

export interface CreditNotesState {
  data: CreditNote[];
  status: API_STATUS;
  view: {
    data?: CreditNoteDetail;
    status: API_STATUS;
  };
  report: {
    data: CreditNoteReportItem[];
    status: API_STATUS;
  };
}

const initialState: CreditNotesState = {
  data: [],
  status: "idle",
  view: {
    data: undefined,
    status: "idle",
  },
  report: {
    data: [],
    status: "idle",
  },
};

export const creditNotesSlice = createSlice({
  name: "creditNotes",
  initialState,
  reducers: {
    refreshCreditNotesList: (state) => {
      state.status = "idle";
    },
    resetCreditNoteView: (state) => {
      state.view.status = "idle";
      state.view.data = undefined;
    },
    refreshCreditNotesReport: (state) => {
      state.report.status = "idle";
    },
  },
  extraReducers(builder) {
    builder.addCase(fetchCreditNotes.pending, (state) => {
      state.status = "pending";
    });
    builder.addCase(fetchCreditNotes.fulfilled, (state, action) => {
      state.status = "success";
      state.data = action.payload;
    });
    builder.addCase(fetchCreditNotes.rejected, (state) => {
      state.status = "error";
    });

    builder.addCase(fetchCreditNoteDetail.pending, (state) => {
      state.view.status = "pending";
    });
    builder.addCase(fetchCreditNoteDetail.fulfilled, (state, action) => {
      state.view.status = "success";
      state.view.data = action.payload;
    });
    builder.addCase(fetchCreditNoteDetail.rejected, (state) => {
      state.view.status = "error";
    });

    builder.addCase(addCreditNote.fulfilled, (state) => {
      state.status = "idle";
    });
    builder.addCase(updateCreditNote.fulfilled, (state) => {
      state.status = "idle";
    });
    builder.addCase(deleteCreditNote.fulfilled, (state) => {
      state.status = "idle";
    });
    builder.addCase(useCreditNote.fulfilled, (state) => {
      state.view.status = "idle";
    });
    builder.addCase(undoCreditUsage.fulfilled, (state) => {
      state.view.status = "idle";
    });

    builder.addCase(fetchCreditNotesReport.pending, (state) => {
      state.report.status = "pending";
    });
    builder.addCase(fetchCreditNotesReport.fulfilled, (state, action) => {
      state.report.status = "success";
      state.report.data = action.payload;
    });
    builder.addCase(fetchCreditNotesReport.rejected, (state) => {
      state.report.status = "error";
    });
  },
});

export const fetchCreditNotes = createAsyncThunk<
  CreditNote[],
  CreditNoteListFilter,
  { state: RootState }
>("creditNotes/fetch", async (filter, thunkAPI) => {
  try {
    const params = new URLSearchParams();
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined && v !== "") params.append(k, String(v));
    });
    params.append("format", "json");
    const response = await axiosInstance.get<
      APIResponseCover<CreditNote[]>,
      AxiosResponse<APIResponseCover<CreditNote[]>>
    >(`CreditNotes/list/?${params.toString()}`);
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) throw new Error(readAPIErrorMessage(e));
    else if (e instanceof Error) throw new Error(e.message);
    return thunkAPI.rejectWithValue(false);
  }
});

export const fetchCreditNoteDetail = createAsyncThunk<
  CreditNoteDetail,
  string,
  { state: RootState }
>("creditNotes/fetchDetail", async (id, thunkAPI) => {
  try {
    const response = await axiosInstance.get<
      APIResponseCover<CreditNoteDetail>,
      AxiosResponse<APIResponseCover<CreditNoteDetail>>
    >(`CreditNotes/${id}/?format=json`);
    return response.data.data;
  } catch (e: unknown) {
    if (e instanceof AxiosError) throw new Error(readAPIErrorMessage(e));
    else if (e instanceof Error) throw new Error(e.message);
    return thunkAPI.rejectWithValue(false);
  }
});

export const addCreditNote = createAsyncThunk<
  { id: string; reference_number: string },
  CreditNoteAddForm,
  { state: RootState }
>("creditNotes/add", async (data, thunkAPI) => {
  try {
    const response = await axiosInstance.post<
      APIResponseCover<{ id: string; reference_number: string }>,
      AxiosResponse<
        APIResponseCover<{ id: string; reference_number: string }>
      >,
      CreditNoteAddForm
    >("CreditNotes/add/?format=json", data);
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) throwErrApi(e);
    else if (e instanceof Error) throw new Error(e.message);
    return thunkAPI.rejectWithValue(false);
  }
});

export const updateCreditNote = createAsyncThunk<
  { status: string },
  CreditNoteUpdateForm,
  { state: RootState }
>("creditNotes/update", async (data, thunkAPI) => {
  try {
    const { id, ...rest } = data;
    await axiosInstance.put<
      APIResponseCover<string>,
      AxiosResponse<APIResponseCover<string>>,
      Omit<CreditNoteUpdateForm, "id">
    >(`CreditNotes/${id}/update/?format=json`, rest);
    return thunkAPI.fulfillWithValue({ status: "idle" });
  } catch (e: unknown) {
    if (e instanceof AxiosError) throwErrApi(e);
    else if (e instanceof Error) throw new Error(e.message);
    return thunkAPI.rejectWithValue(false);
  }
});

export const deleteCreditNote = createAsyncThunk<
  { status: string },
  string,
  { state: RootState }
>("creditNotes/delete", async (id, thunkAPI) => {
  try {
    await axiosInstance.delete<
      APIResponseCover<string>,
      AxiosResponse<APIResponseCover<string>>
    >(`CreditNotes/${id}/delete/?format=json`);
    return thunkAPI.fulfillWithValue({ status: "idle" });
  } catch (e: unknown) {
    if (e instanceof AxiosError) throwErrApi(e);
    else if (e instanceof Error) throw new Error(e.message);
    return thunkAPI.rejectWithValue(false);
  }
});

export const useCreditNote = createAsyncThunk<
  {
    usage_id: string;
    amount_used: string;
    remaining_amount: string;
    credit_note_status: number;
  },
  { id: string; data: UseCreditForm },
  { state: RootState }
>("creditNotes/use", async ({ id, data }, thunkAPI) => {
  try {
    const response = await axiosInstance.post<
      APIResponseCover<any>,
      AxiosResponse<APIResponseCover<any>>,
      UseCreditForm
    >(`CreditNotes/${id}/use/?format=json`, data);
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) throwErrApi(e);
    else if (e instanceof Error) throw new Error(e.message);
    return thunkAPI.rejectWithValue(false);
  }
});

export const undoCreditUsage = createAsyncThunk<
  { remaining_amount: string; credit_note_status: number },
  string,
  { state: RootState }
>("creditNotes/undoUsage", async (usageId, thunkAPI) => {
  try {
    const response = await axiosInstance.delete<
      APIResponseCover<any>,
      AxiosResponse<APIResponseCover<any>>
    >(`CreditNotes/usage/${usageId}/undo/?format=json`);
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) throwErrApi(e);
    else if (e instanceof Error) throw new Error(e.message);
    return thunkAPI.rejectWithValue(false);
  }
});

export const fetchCreditNotesReport = createAsyncThunk<
  CreditNoteReportItem[],
  CreditNoteReportFilter,
  { state: RootState }
>("creditNotes/report", async (filter, thunkAPI) => {
  try {
    const body: Record<string, any> = {};
    Object.entries(filter).forEach(([k, v]) => {
      if (v !== undefined && v !== "") body[k] = v;
    });
    const response = await axiosInstance.post<
      APIResponseCover<CreditNoteReportItem[]>,
      AxiosResponse<APIResponseCover<CreditNoteReportItem[]>>,
      Record<string, any>
    >("ReportsApi/credit-notes-report/?format=json", body);
    return thunkAPI.fulfillWithValue(response.data.data);
  } catch (e: unknown) {
    if (e instanceof AxiosError) throw new Error(readAPIErrorMessage(e));
    else if (e instanceof Error) throw new Error(e.message);
    return thunkAPI.rejectWithValue(false);
  }
});

export const {
  refreshCreditNotesList,
  resetCreditNoteView,
  refreshCreditNotesReport,
} = creditNotesSlice.actions;

export default creditNotesSlice.reducer;
